import { APIComponentAttributes, SortOrder } from '@ardoq/api-types';
import { isNil } from 'lodash';
import { ComponentByIdSortDependencies, SortAttribute } from './sortTypes';
import { Locale, localeCompareNumericLowercase } from '@ardoq/locale';
import { parseToTimestamp } from '@ardoq/date-time';
import { ContextSort, NumericSortOrder } from '@ardoq/data-model';

export const alterSortOrder = (sortOrder: SortOrder) => {
  if (sortOrder === SortOrder.DESC) return SortOrder.DEFAULT;
  if (sortOrder === SortOrder.ASC) return SortOrder.DESC;
  return SortOrder.ASC;
};

export const findSortOrder = (
  sortOrder: SortOrder,
  currentSortByField: string | null,
  newSortByField: string | null
): SortOrder => {
  if (currentSortByField === newSortByField) {
    return alterSortOrder(sortOrder);
  }
  return SortOrder.ASC;
};

type OrderUnsetValuesArgs = {
  aIsNil: boolean;
  bIsNil: boolean;
  orderUnsetLast?: boolean;
};
export const orderUnsetValues = ({
  aIsNil,
  bIsNil,
  orderUnsetLast,
}: OrderUnsetValuesArgs) => {
  const orderFactor = orderUnsetLast ? -1 : 1;

  if (!aIsNil && bIsNil) {
    return 1 * orderFactor;
  }
  if (aIsNil && !bIsNil) {
    return -1 * orderFactor;
  }

  return 0;
};

type ComparableNumericValue = number | boolean;
export const isComparableNumericValue = (
  value: unknown
): value is ComparableNumericValue =>
  typeof value === 'number' || typeof value === 'boolean';

const compareNumericValuesAsc = (
  a: ComparableNumericValue,
  b: ComparableNumericValue
) => (a as number) - (b as number);

export const applySortOrder = (
  comparisonResult: number,
  sortOrder: NumericSortOrder
) =>
  comparisonResult && // prevents negative zero
  comparisonResult * (sortOrder === NumericSortOrder.DESC ? -1 : 1);

/**
 * @returns NaN if the date string is invalid. Check the args before calling this function.
 */
export const validDateFieldValueToDateNumber = (
  value: string | Date | null | undefined
): number | null | undefined => {
  if (isNil(value)) {
    return value;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  return parseToTimestamp(value);
};

/**
 * In date ranges, a single null value (i.e. start is truthy and end is null or vice versa) means infinity,
 * we should substitute the nullish values Infinity if the counterpart is truthy.
 *
 * We also have to convert the strings to date objects, because date strings are not numeric values, and
 * therefore can't be compared with Infinity.
 */
export const getDateRangeValuesForPotentiallyInfiniteDateRanges = (
  componentAttributes: APIComponentAttributes,
  [startFieldName, endFieldName]: [string, string]
) => {
  const start = validDateFieldValueToDateNumber(
    componentAttributes[startFieldName]
  );
  const end = validDateFieldValueToDateNumber(
    componentAttributes[endFieldName]
  );

  if (start && end === null) {
    return [start, Infinity];
  }

  if (end && start === null) {
    return [-Infinity, end];
  }

  return [start, end];
};

type CompareComponentsByDateRangeFieldsArgs = {
  componentAttributesA: APIComponentAttributes;
  componentAttributesB: APIComponentAttributes;
  dateRangeFieldNames: [string, string];
  userLocale: Locale;
  sort: ContextSort;
};
export const compareComponentsByDateRangeFields = ({
  componentAttributesA,
  componentAttributesB,
  dateRangeFieldNames: [startFieldName, endFieldName],
  userLocale,
  sort,
}: CompareComponentsByDateRangeFieldsArgs) => {
  // by clicking on a date range column header, in Table View, user can set
  // the context sort to a date range field, represented by an attribute
  // with a comma delimiter.
  // in such case, we should sort by start date, then end date, and if they're
  // equal, we should fall back to sorting by name.
  // note this also should affect the sort order in the nav tree and various
  // other places.

  const [startValueA, endValueA] =
    getDateRangeValuesForPotentiallyInfiniteDateRanges(componentAttributesA, [
      startFieldName,
      endFieldName,
    ]);

  const [startValueB, endValueB] =
    getDateRangeValuesForPotentiallyInfiniteDateRanges(componentAttributesB, [
      startFieldName,
      endFieldName,
    ]);

  return (
    comparePrimitiveValues(startValueA, startValueB, sort, userLocale) ||
    comparePrimitiveValues(endValueA, endValueB, sort, userLocale) ||
    comparePrimitiveValues(
      componentAttributesA.name,
      componentAttributesB.name,
      sort,
      userLocale
    )
  );
};

export const comparePrimitiveValues = (
  valueA: unknown,
  valueB: unknown,
  { order, orderUnsetLast }: ContextSort,
  userLocale: Locale
) => {
  const aIsNil = isNil(valueA);
  const bIsNil = isNil(valueB);

  if (aIsNil || bIsNil) {
    // is not affected by sort order but by orderUnsetLast
    return orderUnsetValues({
      aIsNil,
      bIsNil,
      orderUnsetLast,
    });
  }

  if (typeof valueA === 'string' && typeof valueB === 'string') {
    return applySortOrder(
      localeCompareNumericLowercase(valueA, valueB, userLocale),
      order
    );
  }

  if (isComparableNumericValue(valueA) && isComparableNumericValue(valueB)) {
    return applySortOrder(compareNumericValuesAsc(valueA, valueB), order);
  }

  return 0;
};

export const getContextSortAttributeName = (sortAttr: string) =>
  sortAttr === SortAttribute.SOURCE || sortAttr === SortAttribute.TARGET
    ? SortAttribute.NAME
    : sortAttr;

export const compareComponents = (
  componentAttributesA: APIComponentAttributes,
  componentAttributesB: APIComponentAttributes,
  sort: ContextSort,
  userLocale: Locale
) => {
  const attributeName = getContextSortAttributeName(sort.attr);

  const rawValueA = componentAttributesA[attributeName];
  const rawValueB = componentAttributesB[attributeName];

  /**
   * In scope data, dates are strings. If we would compare objects in shapes of scope data within a view, it
   * is possible that we would have date objects as values. In such case, we should compare them as numbers.
   */
  const valueA = rawValueA instanceof Date ? rawValueA.valueOf() : rawValueA;
  const valueB = rawValueB instanceof Date ? rawValueB.valueOf() : rawValueB;

  if (valueA === valueB || (!valueA && !valueB)) {
    // Equal or Both falsy -> fallback to sorting on name
    // Fallback sort should always be ascending
    return localeCompareNumericLowercase(
      componentAttributesA.name,
      componentAttributesB.name,
      userLocale
    );
  }

  return comparePrimitiveValues(valueA, valueB, sort, userLocale);
};

/**
 * By clicking on a date range column header, in Table View, user can set the context sort to a date
 * range field, represented by an attribute with a comma delimiter.
 *
 * For performance reasons, provide isDateRangeField as a flag to the sortDependencies.
 */
export const compareComponentsById = (
  componentIdA: string,
  componentIdB: string,
  sortDependencies: ComponentByIdSortDependencies
) => {
  const {
    componentAttributesById,
    sort,
    userLocale,
    isDateRangeField,
    treeOrderIndex,
  } = sortDependencies;

  const componentAttributesA = componentAttributesById[componentIdA];
  const componentAttributesB = componentAttributesById[componentIdB];

  if (!componentAttributesA || !componentAttributesB) return 0;

  if (sort.attr === SortAttribute.ORDER && treeOrderIndex) {
    // `_order` is scoped per parent, so a flat comparison of raw `_order`
    // values mis-orders components from different parents (a parent's
    // first child and another parent's first child both have `_order=0`).
    // The tree-order index encodes a depth-first walk of the workspace
    // tree where each level is sorted by `_order`, matching what the
    // navigator menu renders. See ARD-32236.
    const indexA = treeOrderIndex.get(componentIdA);
    const indexB = treeOrderIndex.get(componentIdB);
    if (indexA !== undefined && indexB !== undefined) {
      return applySortOrder(indexA - indexB, sort.order);
    }
    // Unindexed components sort after indexed ones, regardless of sort
    // direction — mirrors the convention of `orderUnsetLast`.
    if (indexA === undefined && indexB !== undefined) return 1;
    if (indexA !== undefined && indexB === undefined) return -1;
    // Both unindexed: fall through to the existing comparator.
    // For ORDER sort this means a flat `_order` comparison followed by name
    // tiebreak — which still has the per-parent-scoping limitation for
    // orphans, but is the best we can do without tree context.
  }

  if (isDateRangeField) {
    return compareComponentsByDateRangeFields({
      componentAttributesA,
      componentAttributesB,
      dateRangeFieldNames: sort.attr.split(',') as [string, string],
      userLocale,
      sort,
    });
  }

  return compareComponents(
    componentAttributesA,
    componentAttributesB,
    sort,
    userLocale
  );
};

/**
 * Creates a comparator function that sorts items based on their position in a reference array.
 * Items not found in the reference array are treated as having the highest order (appear last).
 *
 * @param key - The key to use for matching items between the arrays
 * @param order - The reference array that defines the desired order
 * @returns A comparator function suitable for Array.sort()
 */
export const sameOrderComparator = <T, Key extends keyof T>(
  key: Key,
  order: T[]
) => {
  const orderMap = new Map<T[Key], number>(
    order.map((item, index) => [item[key], index])
  );

  return (a: T, b: T) => {
    const aIndex = orderMap.get(a[key]);
    const bIndex = orderMap.get(b[key]);
    // If either item is not found in the order array, we treat it as larger
    const aPos = aIndex === undefined ? Number.MAX_SAFE_INTEGER : aIndex;
    const bPos = bIndex === undefined ? Number.MAX_SAFE_INTEGER : bIndex;
    return aPos - bPos;
  };
};
